import { FC } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, ThemeProvider, Typography } from '@mui/material';

import { passengerThemeV2 } from '@mopla/mui';

import { MplButton } from '../../atoms/MplButton/MplButton';
import { MplInputV2 } from '../../atoms/MplInputV2/MplInputV2';

import { IRedeemVoucherFormState } from './hooks';
import { VoucherFormRow } from './styled';

interface IBookingVoucherFormV2Props {
	control: Control<IRedeemVoucherFormState>;
	onSubmit: () => void;
	isSubmitting: boolean;
	isSubmitDisabled: boolean;
	error?: string | null;
	onCodeChange?: () => void;
}

/**
 * V2 Voucher form with simplified UI:
 * - Text button (pseudolink) instead of full button
 * - Error shown in input helper text
 * - Reuses useRedeemVoucherForm hook via control prop
 */
export const BookingVoucherFormV2: FC<IBookingVoucherFormV2Props> = ({
	control,
	onSubmit,
	isSubmitting,
	isSubmitDisabled,
	error,
	onCodeChange,
}) => {
	const { t } = useTranslation('booking');
	const currentCode = useWatch({ control, name: 'code' });
	const showError = Boolean(error) && Boolean(currentCode?.trim());
	return (
		<ThemeProvider theme={passengerThemeV2}>
			<VoucherFormRow>
				<Box flex={1}>
					<Controller
						name="code"
						control={control}
						render={({ field }) => (
							<MplInputV2
								{...field}
								fullWidth
								clearable
								onChange={(e) => {
									e.target.value = e.target.value.trim();
									field.onChange(e);
									onCodeChange?.();
								}}
								label={t('summary.price.voucher.input_label')}
								disabled={isSubmitting}
								error={showError}
								inputProps={{
									'data-testid': 'bookingVoucher-code-input',
								}}
							/>
						)}
					/>
				</Box>
				<MplButton
					variant="pseudolink"
					color="primary"
					onClick={isSubmitDisabled ? undefined : onSubmit}
					disabled={isSubmitDisabled}
					showLoader={isSubmitting}
					testId="bookingVoucher-apply-button"
				>
					{t('summary.price.voucher.apply_button')}
				</MplButton>
			</VoucherFormRow>
			{showError && (
				<Typography
					variant="b2"
					color="error"
					data-testid="bookingVoucher-error-text"
				>
					{error}
				</Typography>
			)}
		</ThemeProvider>
	);
};
